import { useState, useEffect, useRef } from 'react';
import { Search, Plus, X, Building, ChevronDown, Loader2, Phone, Tag } from 'lucide-react';
import { cn } from '../../utils/cn';
import { clientService } from '../../services/clientService';
import Button from '../ui/Button';
import AddressInput from '../ui/AddressInput';

// Mini form for creating new client inline
function CreateClientForm({ companyId, onCreated, onCancel, onFormDataChange }) {
  const [formData, setFormData] = useState({
    clientName: '',
    contactPerson: '',
    contactEmail: '',
    contactPhone: '',
    address: '',
    addressLat: null,
    addressLng: null,
    notes: '',
    abn: '',
    tag: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Notify parent when form has data (for confirmation on close)
  useEffect(() => {
    const hasData = !!(
      formData.clientName?.trim() ||
      formData.contactPerson?.trim() ||
      formData.contactEmail?.trim() ||
      formData.contactPhone?.trim() ||
      formData.address?.trim() ||
      formData.abn?.trim() ||
      formData.tag?.trim()
    );
    onFormDataChange?.(hasData);
  }, [formData, onFormDataChange]);

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (!formData?.clientName?.trim()) {
      setError('Client name is required');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const newClient = await clientService.createClient(formData, companyId);
      onCreated?.(newClient);
    } catch (err) {
      console.error('Failed to create client:', err);
      setError(err?.message || 'Failed to create client');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-4 bg-surface rounded-lg border border-border space-y-3">
      <h4 className="text-sm font-medium text-text-primary">Add New Client</h4>

      {error && (
        <div className="p-2 text-sm text-red-400 bg-red-500/10 rounded">
          {error}
        </div>
      )}

      {/* Client Name */}
      <input
        type="text"
        value={formData?.clientName}
        onChange={(e) => setFormData(prev => ({ ...prev, clientName: e?.target?.value }))}
        placeholder="Client Name *"
        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-background text-sm placeholder-gray-500 focus:outline-none focus:border-primary-orange"
      />

      {/* Contact Person */}
      <input
        type="text"
        value={formData?.contactPerson}
        onChange={(e) => setFormData(prev => ({ ...prev, contactPerson: e?.target?.value }))}
        placeholder="Contact Person"
        className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-background text-sm placeholder-gray-500 focus:outline-none focus:border-primary-orange"
      />

      {/* Contact Info */}
      <div className="grid grid-cols-2 gap-2">
        <input
          type="email"
          value={formData?.contactEmail}
          onChange={(e) => setFormData(prev => ({ ...prev, contactEmail: e?.target?.value }))}
          placeholder="Email"
          className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-background text-sm placeholder-gray-500 focus:outline-none focus:border-primary-orange"
        />
        <div className="relative flex items-center">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
          <span className="absolute left-9 top-1/2 -translate-y-1/2 text-text-secondary text-sm">+61</span>
          <input
            type="tel"
            inputMode="numeric"
            pattern="[0-9]*"
            value={formData?.contactPhone}
            onChange={(e) => {
              const value = e?.target?.value?.replace(/[^0-9]/g, '');
              setFormData(prev => ({ ...prev, contactPhone: value }));
            }}
            placeholder="412 345 678"
            className="w-full pl-[4.5rem] pr-3 py-2 bg-white border border-gray-300 rounded-lg text-background text-sm placeholder-gray-500 focus:outline-none focus:border-primary-orange"
          />
        </div>
      </div>

      {/* Address */}
      <AddressInput
        value={{
          address: formData?.address || '',
          lat: formData?.addressLat,
          lng: formData?.addressLng
        }}
        onChange={({ address, lat, lng }) => {
          setFormData(prev => ({
            ...prev,
            address: address || '',
            addressLat: lat,
            addressLng: lng
          }));
        }}
        placeholder="Search for address..."
        compact={true}
        allowMapToggle={true}
        mapHeight="200px"
      />

      {/* ABN and Tag */}
      <div className="grid grid-cols-2 gap-2">
        <input
          type="text"
          value={formData?.abn}
          onChange={(e) => {
            const value = e?.target?.value?.replace(/[^0-9\s]/g, '');
            setFormData(prev => ({ ...prev, abn: value }));
          }}
          placeholder="ABN (optional)"
          className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-background text-sm placeholder-gray-500 focus:outline-none focus:border-primary-orange"
        />
        <div className="relative flex items-center">
          <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
          <input
            type="text"
            value={formData?.tag}
            onChange={(e) => {
              const value = e?.target?.value?.toUpperCase()?.replace(/[^A-Z0-9]/g, '').slice(0, 8);
              setFormData(prev => ({ ...prev, tag: value }));
            }}
            placeholder="Tag (e.g. ACME)"
            className="w-full pl-10 pr-3 py-2 bg-white border border-gray-300 rounded-lg text-background text-sm placeholder-gray-500 focus:outline-none focus:border-primary-orange"
          />
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="default"
          size="sm"
          onClick={handleSubmit}
          disabled={isSubmitting || !formData?.clientName?.trim()}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-1" />
              Creating...
            </>
          ) : (
            'Add'
          )}
        </Button>
      </div>
    </div>
  );
}

// Main ClientSelect component
export default function ClientSelect({
  value, // Selected client object or ID
  onChange,
  companyId,
  className,
  placeholder = 'Search or select client...',
  disabled = false,
  error
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [clients, setClients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [formHasData, setFormHasData] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const containerRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Load clients
  const loadClients = async () => {
    if (!companyId) return;

    setIsLoading(true);
    try {
      const data = await clientService.getCompanyClients(companyId);
      setClients(data || []);
    } catch (err) {
      console.error('Failed to load clients:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Search clients
  const searchClients = async (term) => {
    if (!companyId || !term) {
      loadClients();
      return;
    }

    setIsLoading(true);
    try {
      const data = await clientService.searchClients(companyId, term);
      setClients(data || []);
    } catch (err) {
      console.error('Failed to search clients:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Load on mount
  useEffect(() => {
    if (companyId) {
      loadClients();
    }
  }, [companyId]);

  // Debounced search
  const handleSearchChange = (e) => {
    const term = e?.target?.value;
    setSearchTerm(term);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchClients(term);
    }, 300);
  };

  // Click outside to close (with confirmation if form has data)
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e?.target)) {
        // If create form is open and has data, show confirmation
        if (showCreateForm && formHasData) {
          setShowDiscardConfirm(true);
          return;
        }
        setIsOpen(false);
        setShowCreateForm(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCreateForm, formHasData]);

  // Handle discard confirmation
  const handleConfirmDiscard = () => {
    setShowDiscardConfirm(false);
    setShowCreateForm(false);
    setFormHasData(false);
    setIsOpen(false);
  };

  const handleCancelDiscard = () => {
    setShowDiscardConfirm(false);
  };

  // Handle selection
  const handleSelect = (client) => {
    onChange?.(client);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Handle new client created
  const handleClientCreated = (newClient) => {
    setClients(prev => [newClient, ...prev]);
    handleSelect(newClient);
    setShowCreateForm(false);
  };

  // Clear selection
  const handleClear = () => {
    onChange?.(null);
    setSearchTerm('');
  };

  const selectedClient = typeof value === 'object' ? value : clients?.find(c => c?.id === value);

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Selected Value / Search Input */}
      <div
        onClick={() => !disabled && setIsOpen(true)}
        className={cn(
          'flex items-center gap-2 px-3 py-2 bg-surface border rounded-lg cursor-pointer transition-colors',
          isOpen ? 'border-primary-orange' : 'border-border',
          error && 'border-red-500',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      >
        {selectedClient ? (
          <>
            <Building className="w-4 h-4 text-white-shade-2 flex-shrink-0" />
            <span className="flex-1 text-text-primary truncate">
              {selectedClient?.clientName}
              {selectedClient?.contactPerson && (
                <span className="text-text-secondary ml-1">
                  ({selectedClient?.contactPerson})
                </span>
              )}
            </span>
            {!disabled && (
              <button
                type="button"
                onClick={(e) => {
                  e?.stopPropagation();
                  handleClear();
                }}
                className="text-white-shade-2 hover:text-text-primary"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </>
        ) : (
          <>
            <Search className="w-4 h-4 text-white-shade-2 flex-shrink-0" />
            <span className="flex-1 text-white-shade-2">{placeholder}</span>
            <ChevronDown className="w-4 h-4 text-white-shade-2" />
          </>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-xs text-red-400">{error}</p>
      )}

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-lg shadow-xl overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-border bg-background">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary" />
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder="Search clients..."
                autoFocus
                className="w-full pl-9 pr-3 py-2 bg-surface border border-border rounded text-text-primary text-sm placeholder-gray-500 focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          {/* Create New Option */}
          {showCreateForm ? (
            <div className="p-2 border-b border-border bg-background">
              <CreateClientForm
                companyId={companyId}
                onCreated={handleClientCreated}
                onCancel={() => {
                  if (formHasData) {
                    setShowDiscardConfirm(true);
                  } else {
                    setShowCreateForm(false);
                  }
                }}
                onFormDataChange={setFormHasData}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowCreateForm(true)}
              className="w-full px-4 py-2 text-left text-sm text-primary-orange hover:bg-surface flex items-center gap-2 border-b border-border bg-background"
            >
              <Plus className="w-4 h-4" />
              Add New Client
            </button>
          )}

          {/* Results List */}
          <div className="max-h-48 overflow-y-auto bg-background">
            {isLoading ? (
              <div className="px-4 py-3 text-center text-text-secondary text-sm">
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                Loading...
              </div>
            ) : clients?.length > 0 ? (
              clients?.map(client => (
                <button
                  key={client?.id}
                  type="button"
                  onClick={() => handleSelect(client)}
                  className={cn(
                    'w-full px-4 py-2 text-left hover:bg-surface transition-colors',
                    selectedClient?.id === client?.id && 'bg-surface'
                  )}
                >
                  <div className="flex items-center gap-2">
                    <Building className="w-4 h-4 text-text-secondary flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-text-primary truncate">{client?.clientName}</p>
                      {client?.contactPerson && (
                        <p className="text-xs text-text-secondary truncate">
                          {client?.contactPerson}
                        </p>
                      )}
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="px-4 py-3 text-center text-text-secondary text-sm">
                {searchTerm ? 'No results found' : 'No clients yet'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Discard Confirmation Dialog */}
      {showDiscardConfirm && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={handleCancelDiscard}
          />
          <div className="relative bg-surface border border-border rounded-lg p-4 shadow-xl max-w-sm mx-4">
            <h4 className="text-sm font-medium text-text-primary mb-2">Discard changes?</h4>
            <p className="text-sm text-text-secondary mb-4">
              You have unsaved data in the form. Are you sure you want to discard it?
            </p>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleCancelDiscard}
              >
                Keep Editing
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={handleConfirmDiscard}
              >
                Discard
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
